import axiosInstance from "../../utilities/Axios";

// Calls to the Laravel proxy (see aaa-agent/docs/api-reference.md, "Laravel proxy route").
// axiosInstance's base URL already ends in /api, so paths start at /v1.

export const CHAT_MAX_LENGTH = 4000;
export const SESSION_STORAGE_KEY = "assistant_session_id";

export const CHAT_PATH = "/v1/assistant/chat";
export const sessionPath = (sessionId: string) => `/v1/assistant/sessions/${encodeURIComponent(sessionId)}`;

export interface ChatResult {
  session_id: string;
  reply: string;
  request_id: string;
  tools_used: string[];
}

export type AssistantErrorKind =
  | "rate_limit" // 429, per minute
  | "daily_limit" // 429, per day
  | "unavailable" // 502
  | "timeout" // 504
  | "busy" // 409
  | "disabled" // 503: kill switch, hide the widget
  | "forbidden" // 403: hide the widget
  | "expired" // 401: token expired, sign out
  | "invalid" // 422
  | "network" // no response at all
  | "unknown";

export class AssistantApiError extends Error {
  kind: AssistantErrorKind;
  status?: number;

  constructor(kind: AssistantErrorKind, status?: number, message?: string) {
    super(message || kind);
    this.name = "AssistantApiError";
    this.kind = kind;
    this.status = status;
  }
}

// Messages from user-flows.md, Flow 8 and Flow 4.
export const ERROR_MESSAGES: Record<AssistantErrorKind, string> = {
  rate_limit: "Too many messages. Wait a moment and try again.",
  daily_limit: "You've reached today's limit for the assistant. It resets tomorrow.",
  unavailable: "The assistant could not complete this request. Try again.",
  timeout: "This took too long. Try a narrower question.",
  busy: "A reply is still being prepared in this conversation. Please wait a moment.",
  disabled: "The assistant is turned off.",
  forbidden: "You do not have access to the assistant.",
  expired: "Your session has expired. Please sign in again.",
  invalid: "That message could not be sent. Check it and try again.",
  network: "Could not reach the assistant. Check your connection and try again.",
  unknown: "The assistant could not complete this request. Try again.",
};

const bodyText = (data: any): string => {
  if (!data) return "";
  if (typeof data === "string") return data;
  return String(data.error ?? data.message ?? data.detail ?? "");
};

export const toAssistantError = (err: any): AssistantApiError => {
  if (err instanceof AssistantApiError) return err;
  const status: number | undefined = err?.response?.status;
  if (!status) return new AssistantApiError("network");
  switch (status) {
    case 401:
      return new AssistantApiError("expired", status);
    case 403:
      return new AssistantApiError("forbidden", status);
    case 409:
      return new AssistantApiError("busy", status);
    case 422:
      return new AssistantApiError("invalid", status);
    case 429:
      // The proxy uses 429 for both the per-minute limit and the daily quota.
      return new AssistantApiError(
        /daily|today|day\b/i.test(bodyText(err.response.data)) ? "daily_limit" : "rate_limit",
        status
      );
    case 502:
      return new AssistantApiError("unavailable", status);
    case 503:
      return new AssistantApiError("disabled", status);
    case 504:
      return new AssistantApiError("timeout", status);
    default:
      return new AssistantApiError("unknown", status);
  }
};

// The proxy answers {"status": "ok", "result": {...}}; accept the bare result too.
const unwrap = (data: any): any => (data && typeof data === "object" && "result" in data ? data.result : data);

export const sendChat = async (message: string, sessionId: string | null): Promise<ChatResult> => {
  let res: any;
  try {
    res = await axiosInstance.post(CHAT_PATH, { message, session_id: sessionId });
  } catch (err) {
    throw toAssistantError(err);
  }
  const result = unwrap(res?.data);
  if (!result || typeof result.reply !== "string" || typeof result.session_id !== "string") {
    throw new AssistantApiError("unavailable", res?.status);
  }
  return {
    session_id: result.session_id,
    reply: result.reply,
    request_id: typeof result.request_id === "string" ? result.request_id : "",
    tools_used: Array.isArray(result.tools_used) ? result.tools_used : [],
  };
};

// `token` is only for sign-out: logout() has already removed the token from localStorage
// (which the axios interceptor reads) by the time this runs, so the caller passes the one it kept.
export const deleteSession = async (sessionId: string, token?: string | null): Promise<void> => {
  try {
    await axiosInstance.delete(sessionPath(sessionId), token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
  } catch (err) {
    throw toAssistantError(err);
  }
};

// sessionStorage: one conversation per browser tab. It can throw (blocked storage), so wrap it.
export const getStoredSessionId = (): string | null => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredSessionId = (sessionId: string) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    /* ignore */
  }
};

export const clearStoredSessionId = () => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
